import axios from 'axios';
import { API_URL } from '../config/api';
import SessionService from './SessionService';
import type { PagedResult, ContactListDTO } from '../types/pagedAndDtos';
import { resolveContactDisplayName } from '../utils/formatters';
import { downloadXlsxResponse, readFetchErrorMessage } from '../utils/downloadBlob';

// Interface para a resposta da API
interface ApiResponse<T> {
  message: string;
  code: string;
  data: T[];
  pagination?: PaginationInfo;
}

export interface PaginationInfo {
  limit: number;
  offset: number;
  total: number;
  hasMore: boolean;
  nextOffset: number;
}

function isTransientNetworkError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  const msg = String(error.message || '').toLowerCase();
  return (
    error.code === 'ERR_NETWORK' ||
    msg.includes('network error') ||
    msg.includes('chunked') ||
    msg.includes('incomplete')
  );
}

async function postJsonWithRetry<T>(
  url: string,
  payload: unknown,
  headers: Record<string, string>,
  retries = 2
): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await axios.post<T>(url, payload, {
        headers,
        timeout: 60000,
      });
      return res.data;
    } catch (error) {
      lastError = error;
      if (!isTransientNetworkError(error) || attempt === retries) {
        throw error;
      }
      await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
    }
  }
  throw lastError;
}

// Interface para o contato
export interface Contact {
  isViewed: boolean;
  is_viewed: any;
  id: number;
  name: string;
  tagId: number | null;
  number: string;
  avatarUrl: string | null;
  email: string;
  notes: string | null;
  sectorId: number;
  isActive: boolean;
  priority: string;
  assignedTo: number | null;
  aiActive: number;
  createdAt: string;
  updatedAt: string;
  contactStatus: 'Novo' | 'Qualificado' | 'Não qualificado' | string;

  isOfficial: boolean;
  archived?: boolean;
  isFavorite?: boolean;
  lastMessage?: string;
  lastMessageTime?: string;
  unreadCount?: number;
  /** Cliente falou por último (72h) e ninguém respondeu; null/undefined = fora da janela ou desconhecido. */
  lastMessageFromCustomer?: boolean | null;
  /** Agente de IA que está atendendo (só com aiActive = 1); null = desconhecido. */
  aiAgentId?: number | null;
  aiAgentName?: string | null;
  isGroup?: boolean;
  /** Preenchido quando o contato é canal Spark (áudio/outbound via API Spark). */
  sparkChatId?: string | null;
  sparkEntrypointId?: string | null;
  /** Telefone da linha Spark (cadastro opcional no admin). */
  sparkLinePhone?: string | null;
  sparkLineLabel?: string | null;
  channel?: 'spark' | null;
  /** E-mail em Conversas: onde foi a última conversa e quais canais o cliente tem. */
  lastMessageChannel?: 'email' | 'whatsapp' | 'instagram' | 'messenger' | 'webchat' | null;
  hasEmail?: boolean;
  /** Cliente tem identidade do Instagram (escreveu pelo Instagram). */
  hasInstagram?: boolean;
  /** Cliente tem identidade do Messenger (escreveu para uma Página conectada). */
  hasMessenger?: boolean;
  /** Cliente tem identidade de site (falou pelo chat do site). */
  hasWebchat?: boolean;
  hasWhatsApp?: boolean;
}

export interface CreateContactRequestDTO {
  name: string;
  phoneNumber: string;
  email: string;
  sectorId: number;
  notes?: string;
  contactStatus?: 'Novo' | 'Qualificado' | 'Não qualificado' | string;
}

export interface UpdateContactRequestDTO {
  name: string;
  tagId: number | null;
  phoneWhatsapp: string;
  avatarUrl: string | null;
  email: string;
  notes: string | null;
  sectorId: number;
  isActive: boolean;
  priority: string;
  /** Omitir em update parcial: o backend preserva o valor atual em vez de religar a IA. */
  aiActive?: number;
  assignedTo: number | null;
  isFavorite?: boolean;
  contactStatus?: 'Novo' | 'Qualificado' | 'Não qualificado' | string;
}

export interface UpdateResponsibleRequestDTO {
  responsibleId: number;
}

export interface ArchiveContactRequestDTO {
  contactId: number;
  archived: boolean;
}

export interface ListContactsRequestDTO {
  sectorId: number;
  includeArchived: boolean;
  onlyArchived: boolean;
}

/** Normalizes Go / C# contact payloads into Contact shape. */
export function mapApiContactToContact(raw: Record<string, unknown> | null | undefined): Contact | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = (r.id ?? r.Id) as number | undefined;
  if (id == null || Number.isNaN(Number(id))) return null;
  const viewed = Boolean(r.isViewed ?? r.is_viewed ?? r.IsViewed ?? true);
  const number = String(r.number ?? r.Number ?? '');
  const rawName = String(r.name ?? r.Name ?? '');
  return {
    id: Number(id),
    name: resolveContactDisplayName(undefined, rawName, number),
    tagId: (r.tagId ?? r.TagId ?? null) as number | null,
    number,
    avatarUrl: (r.avatarUrl ?? r.AvatarUrl ?? r.avatarURL ?? null) as string | null,
    email: String(r.email ?? r.Email ?? ''),
    notes: (r.notes ?? r.Notes ?? null) as string | null,
    sectorId: Number(r.sectorId ?? r.SectorId ?? 0),
    isActive: Boolean(r.isActive ?? r.IsActive ?? true),
    priority: String(r.priority ?? r.Priority ?? 'medium'),
    aiActive: Number(r.aiActive ?? r.AiActive ?? r.ai_active ?? 0),
    assignedTo: (r.assignedTo ?? r.AssignedTo ?? null) as number | null,
    createdAt: String(r.createdAt ?? r.CreatedAt ?? new Date().toISOString()),
    updatedAt: String(r.updatedAt ?? r.UpdatedAt ?? new Date().toISOString()),
    contactStatus: (r.contactStatus ?? r.ContactStatus ?? 'Novo') as Contact['contactStatus'],
    isViewed: viewed,
    is_viewed: viewed,
    isOfficial: Boolean(r.isOfficial ?? r.IsOfficial ?? false),
    archived: Boolean(r.archived ?? r.Archived ?? false),
    isFavorite: Boolean(r.isFavorite ?? r.IsFavorite ?? false),
    lastMessage: (r.lastMessage ?? r.LastMessage ?? undefined) as string | undefined,
    lastMessageTime: (r.lastMessageTime ?? r.LastMessageTime ?? r.last_message_time ?? undefined) as string | undefined,
    unreadCount: Number(r.unreadCount ?? r.UnreadCount ?? r.unread_count ?? 0) || 0,
    lastMessageFromCustomer: (() => {
      const v = r.lastMessageFromCustomer ?? r.LastMessageFromCustomer;
      return typeof v === 'boolean' ? v : null;
    })(),
    aiAgentId: (() => {
      const v = Number(r.aiAgentId ?? r.AiAgentId ?? 0);
      return v > 0 ? v : null;
    })(),
    aiAgentName: (() => {
      const v = r.aiAgentName ?? r.AiAgentName;
      return typeof v === 'string' && v.trim() ? v.trim() : null;
    })(),
    isGroup: Boolean(r.isGroup ?? r.IsGroup ?? r.is_group ?? false),
    sparkChatId: (r.sparkChatId ?? r.SparkChatId ?? r.spark_chat_id ?? null) as string | null,
    sparkEntrypointId: (r.sparkEntrypointId ?? r.SparkEntrypointId ?? r.spark_entrypoint_id ?? null) as string | null,
    sparkLinePhone: (r.sparkLinePhone ?? r.SparkLinePhone ?? r.spark_line_phone ?? null) as string | null,
    sparkLineLabel: (r.sparkLineLabel ?? r.SparkLineLabel ?? r.spark_line_label ?? null) as string | null,
    channel: (() => {
      const ch = (r.channel ?? r.Channel ?? null) as string | null;
      if (ch === 'spark') return 'spark' as const;
      const sparkId = r.sparkChatId ?? r.SparkChatId ?? r.spark_chat_id;
      return sparkId ? ('spark' as const) : null;
    })(),
    lastMessageChannel: (() => {
      const v = r.lastMessageChannel ?? r.LastMessageChannel;
      return v === 'email' || v === 'whatsapp' || v === 'instagram' || v === 'messenger' || v === 'webchat' ? v : null;
    })(),
    hasEmail: Boolean(r.hasEmail ?? r.HasEmail ?? false),
    hasInstagram: Boolean(r.hasInstagram ?? r.HasInstagram ?? false),
    hasMessenger: Boolean(r.hasMessenger ?? r.HasMessenger ?? false),
    hasWebchat: Boolean(r.hasWebchat ?? r.HasWebchat ?? false),
    hasWhatsApp: (() => {
      const v = r.hasWhatsApp ?? r.HasWhatsApp;
      return typeof v === 'boolean' ? v : Boolean(String(r.number ?? r.Number ?? '').trim());
    })(),
  };
}

export class DuplicateContactError extends Error {
  readonly existingContact: Contact;
  constructor(existingContact: Contact) {
    super('DUPLICATE_CONTACT');
    this.name = 'DuplicateContactError';
    this.existingContact = existingContact;
  }
}

const CHAT_LIST_PAGE_SIZE = 1000;
/** Parallel page fetches per batch (balance speed vs load on API/DB). */
const CHAT_LIST_PARALLEL_CONCURRENCY = 6;

/** Small first request so the chat shell shows contacts immediately; full list follows via `fetchAllChatContactsForSector`. */
export const CHAT_LIST_QUICK_LIMIT = 250;

type ContactsListApiShape = {
  data?: Record<string, unknown>[];
  pagination?: PaginationInfo;
};

function contactsFromListPayload(res: unknown): Contact[] {
  const r = res as ContactsListApiShape;
  const batch = Array.isArray(r?.data) ? r.data : [];
  const out: Contact[] = [];
  for (const raw of batch) {
    const c = mapApiContactToContact(raw);
    if (c) out.push(c);
  }
  return out;
}

/** Fast first paint — same filters as the full chat list, capped rows. */
export async function fetchChatContactsQuick(
  sectorId: number,
  includeArchived: boolean,
  onlyArchived: boolean
): Promise<Contact[]> {
  const res = await getContactsWithFilters(
    sectorId,
    includeArchived,
    onlyArchived,
    CHAT_LIST_QUICK_LIMIT,
    0,
    'chat'
  );
  return contactsFromListPayload(res);
}

/**
 * Loads every chat-list page for a sector. Uses parallel requests when the API returns `pagination.total`
 * so many pages are not fetched one-after-another (which was very slow).
 */
export async function fetchAllChatContactsForSector(
  sectorId: number,
  includeArchived: boolean,
  onlyArchived: boolean
): Promise<Contact[]> {
  const pageSize = CHAT_LIST_PAGE_SIZE;

  const fetchPage = async (offset: number) => {
    const res = await getContactsWithFilters(
      sectorId,
      includeArchived,
      onlyArchived,
      pageSize,
      offset,
      'chat'
    );
    const contacts = contactsFromListPayload(res);
    const pag = (res as unknown as ContactsListApiShape).pagination;
    return { offset, contacts, pagination: pag };
  };

  const first = await fetchPage(0);
  if (!first.pagination?.hasMore || first.contacts.length === 0) {
    return first.contacts;
  }

  const total = first.pagination.total;
  const canParallel =
    typeof total === 'number' &&
    Number.isFinite(total) &&
    total > pageSize;

  if (!canParallel) {
    const out = [...first.contacts];
    let offset = first.pagination.nextOffset ?? pageSize;
    for (;;) {
      const next = await fetchPage(offset);
      out.push(...next.contacts);
      if (!next.pagination?.hasMore || next.contacts.length === 0) break;
      offset = next.pagination?.nextOffset ?? offset + pageSize;
    }
    return out;
  }

  const offsets: number[] = [];
  for (let o = pageSize; o < total; o += pageSize) {
    offsets.push(o);
  }

  const byOffset = new Map<number, Contact[]>();
  byOffset.set(0, first.contacts);

  for (let i = 0; i < offsets.length; i += CHAT_LIST_PARALLEL_CONCURRENCY) {
    const slice = offsets.slice(i, i + CHAT_LIST_PARALLEL_CONCURRENCY);
    const pages = await Promise.all(slice.map((off) => fetchPage(off)));
    for (const p of pages) {
      byOffset.set(p.offset, p.contacts);
    }
  }

  const ordered: Contact[] = [];
  for (let o = 0; o < total; o += pageSize) {
    const chunk = byOffset.get(o);
    if (chunk?.length) ordered.push(...chunk);
  }
  return ordered;
}

/** Lista paginada de contatos (DTO enxuto) - use em telas com muitos contatos. */
export const getContactsPaged = async (
  sectorId: number,
  pageNumber: number = 1,
  pageSize: number = 50,
  search?: string
): Promise<PagedResult<ContactListDTO>> => {
  const token = SessionService.getToken();
  if (!token) throw new Error('Authentication token not found');

  const params: Record<string, number | string> = { sectorId, pageNumber, pageSize };
  if (search != null && search.trim() !== '') params.search = search.trim();

  const response = await axios.get<PagedResult<ContactListDTO>>(`${API_URL}/Contact/paged`, {
    params,
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const getContacts = async (sectorId: number): Promise<ApiResponse<Contact>> => {
  try {
    console.log('Iniciando getContacts:', { sectorId });
    const token = SessionService.getToken();
    console.log('Token recuperado:', !!token);

    if (!token) {
      throw new Error('Authentication token not found');
    }

    // C# / Go endpoints: /Contact/sector/{sectorId} returns {message,code,data} or {status,message,data} wrappers (possibly nested)
    const candidates = [
      `${API_URL}/Contact/sector/${sectorId}`,
      `${API_URL.replace(/\/api$/i, '/api/v1')}/Contact/sector/${sectorId}`,
    ];
    let body: any = {};
    for (const url of candidates) {
      try {
        const response = await axios.get<any>(url, {
          headers: {
            'Accept': '*/*',
            'Authorization': `Bearer ${token}`
          },
        });
        body = response.data || {};
        break;
      } catch {
        // try next
      }
    }

    console.log('Resposta recebida getContacts:', { status: body ? 'ok' : 'fail', dataKeys: Object.keys(body || {}) });

    // Robust extraction for {message,code,data}, {status,message,data:{...}}, Go double-wrap etc.
    const extractArr = (b: any): any[] => {
      if (!b) return [];
      if (Array.isArray(b)) return b;
      let d = b.data ?? b.Data ?? b;
      if (Array.isArray(d)) return d;
      if (d && typeof d === 'object') {
        if (Array.isArray(d.data)) return d.data;
        if (Array.isArray(d.Data)) return d.Data;
        if (Array.isArray(d.contacts)) return d.contacts;
        if (Array.isArray(d.Contacts)) return d.Contacts;
        if ((d.message || d.code) && (Array.isArray(d.data) || Array.isArray(d.Data))) {
          return Array.isArray(d.data) ? d.data : d.Data;
        }
      }
      if (b.status && b.data) {
        const dd = b.data;
        if (Array.isArray(dd)) return dd;
        if (Array.isArray(dd?.data)) return dd.data;
      }
      return [];
    };
    const dataArr = extractArr(body);
    const msg = body?.message ?? body?.Message ?? body?.status ?? 'Success';
    const code = body?.code ?? body?.Code ?? '200';
    return { message: msg, code, data: dataArr } as ApiResponse<Contact>;
  } catch (error) {
    console.error('Erro detalhado em getContacts:', {
      error,
      message: error instanceof Error ? error.message : 'Erro desconhecido',
    });
    throw new Error('Falha ao obter contatos: ' + error);
  }
};

export const getContactById = async (id: number): Promise<Contact | null> => {
  try {
    const token = SessionService.getToken();

    if (!token) {
      throw new Error('Authentication token not found');
    }

    const response = await axios.get(`${API_URL}/Contact/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    const body = response.data || {};
    const inner = body.data ?? body.Data ?? body;
    const mapped = mapApiContactToContact(inner as any);
    return mapped;
  } catch (error) {
    console.error('Error fetching contact:', error);
    return null;
  }
};

export const createContact = async (contact: CreateContactRequestDTO): Promise<Contact | null> => {
  const token = SessionService.getToken();
  if (!token) throw new Error('Authentication token not found');

  const response = await axios.post(`${API_URL}/Contact`, contact, {
    headers: { Authorization: `Bearer ${token}` },
    validateStatus: (s) => (s >= 200 && s < 300) || s === 409,
  });

  if (response.status === 409) {
    const body = response.data as { message?: string; data?: { existingContact?: Record<string, unknown> } };
    const raw = body?.data?.existingContact;
    const mapped = raw ? mapApiContactToContact(raw) : null;
    if (mapped) throw new DuplicateContactError(mapped);
    throw new Error(body?.message || 'A contact with this phone number already exists in this sector');
  }

  const body = response.data as { data?: Record<string, unknown> } | Record<string, unknown>;
  const inner = (body as { data?: Record<string, unknown> }).data ?? body;
  return mapApiContactToContact(inner as Record<string, unknown>);
};

export const updateContact = async (id: number, contact: UpdateContactRequestDTO): Promise<Contact> => {
  try {
    const token = SessionService.getToken();

    if (!token) {
      throw new Error('Authentication token not found');
    }

    const response = await axios.put<Contact>(
      `${API_URL}/Contact/${id}`,
      contact,
      {
        headers: {
          'Accept': '*/*',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating contact:', error);
    throw new Error(`Failed to update contact with id ${id}: ${error}`);
  }
};

export const toggleFavorite = async (contactId: number, isFavorite: boolean): Promise<any> => {
  try {
    const token = SessionService.getToken();

    if (!token) {
      throw new Error('Authentication token not found');
    }

    // No dedicated favorite endpoint; route through update
    const response = await axios.put(
      `${API_URL}/Contact/${contactId}`,
      { isFavorite },
      {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Erro ao favoritar/desfavoritar contato:', error);
    return { success: true, isFavorite };
  }
};



export const updateResponsible = async (id: number, data: UpdateResponsibleRequestDTO) => {
  try {
    const response = await axios.put(`${API_URL}/Contact/${id}/responsible`, data, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SessionService.getToken()}`
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(`Failed to update responsible for contact with id ${id}: ${error}`);
  }
};

export const getContact = async (id: number): Promise<Contact> => {
  try {
    const response = await axios.get(`${API_URL}/Contact/${id}`, {
      headers: {
        Accept: 'application/json',
        'Authorization': `Bearer ${SessionService.getToken()}`
      },
    });
    // unwrap if wrapped
    const body = response.data || {};
    return (body.data ?? body.Data ?? body) as Contact;
  } catch (error) {
    throw new Error(`Failed to get contact with id ${id}: ${error}`);
  }
};

export const deleteContact = async (id: number) => {
  try {
    const response = await axios.delete(`${API_URL}/Contact/${id}`, {
      headers: {
        'Authorization': `Bearer ${SessionService.getToken()}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('[deleteContact]', error);
    throw new Error('Could not remove this contact. Please try again.');
  }
};

export const deleteContactFromSector = async (contactId: number, sectorId: number) => {
  try {
    const token = SessionService.getToken();

    if (!token) {
      throw new Error('Authentication token not found');
    }

    const url = `${API_URL}/Contact/${contactId}/sector/${sectorId}`;
    const response = await axios.delete(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    throw new Error(`Failed to delete contact ${contactId} from sector ${sectorId}: ${error}`);
  }
};

export const markContactAsViewed = async (sectorId: number, contactId: number) => {
  try {
    const token = SessionService.getToken();

    if (!token) {
      throw new Error('Authentication token not found');
    }

    await axios.post(
      `${API_URL}/Contact/mark-viewed`,
      { sectorId, contactId },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      }
    ).catch(() => {/* non-fatal */});
    return true;
  } catch (error) {
    console.error('Erro ao marcar contato como visualizado:', error);
    return false;
  }
};

export const archiveContact = async (contactId: number, archived: boolean): Promise<any> => {
  try {
    const token = SessionService.getToken();

    if (!token) {
      throw new Error('Authentication token not found');
    }

    // No dedicated archive endpoint; use update contact (C# supports archived via update DTO in practice)
    const response = await axios.put(
      `${API_URL}/Contact/${contactId}`,
      { archived },
      {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Erro ao arquivar/desarquivar contato:', error);
    // Do not break chat UI
    return { success: true, archived };
  }
};

/** Modo da listagem em POST /contacts/list: chat = conversa (última msg); crm = Kanban; picker = seleção leve. */
export type ContactsListView = 'chat' | 'crm' | 'picker' | 'groups';

/** Limite máximo no servidor para view picker (broadcast / grupos / listas grandes). */
export const CONTACTS_PICKER_MAX_LIMIT = 50000;

/**
 * Downloads an .xlsx with columns Name + Phone Number (same as the import template) for the given sector.
 * Use to move contacts between accounts via Import Contacts.
 */
export async function exportContactsForImport(sectorId: number): Promise<void> {
  const token = SessionService.getToken();
  if (!token) {
    throw new Error('Authentication token not found');
  }
  if (sectorId <= 0) {
    throw new Error('Invalid sector');
  }

  const response = await fetch(
    `${API_URL}/Contact/export?sectorId=${encodeURIComponent(String(sectorId))}`,
    {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (!response.ok) {
    throw new Error(await readFetchErrorMessage(response, 'Export failed'));
  }

  await downloadXlsxResponse(response, `contacts_import_ready_sector${sectorId}.xlsx`);
}

/** Normalize any backend list response (C# PagedResult / ListResponse / direct array / Go {data}) into frontend ApiResponse shape used by chat. */
function toApiResponseFromAny(raw: any, fallbackLimit: number, fallbackOffset: number): ApiResponse<Contact> {
  if (!raw) return { message: 'Success', code: '200', data: [] };
  // Direct PagedResult from C# /paged
  if (raw && Array.isArray(raw.items)) {
    const items = (raw.items || []).map((r: any) => mapApiContactToContact(r)).filter(Boolean) as Contact[];
    const total = typeof raw.totalCount === 'number' ? raw.totalCount : items.length;
    const hasMore = !!raw.hasNextPage;
    const nextOffset = fallbackOffset + items.length;
    return {
      message: 'Success',
      code: '200',
      data: items,
      pagination: { limit: raw.pageSize ?? fallbackLimit, offset: fallbackOffset, total, hasMore, nextOffset }
    };
  }
  // Wrapped responses: support data/Data + pagination/Pagination
  const dataArr = Array.isArray(raw?.data) ? raw.data : (Array.isArray(raw?.Data) ? raw.Data : (Array.isArray(raw) ? raw : []));
  const mapped = dataArr.map((r: any) => mapApiContactToContact(r)).filter(Boolean) as Contact[];
  const pagRaw = raw?.pagination || raw?.Pagination || null;
  const total = pagRaw?.total ?? pagRaw?.Total ?? raw?.totalCount ?? mapped.length;
  const hasMore = pagRaw ? Boolean(pagRaw.hasMore ?? pagRaw.HasMore ?? pagRaw?.hasNextPage) : (mapped.length === (raw?.pageSize ?? fallbackLimit));
  const nextOff = pagRaw?.nextOffset ?? pagRaw?.NextOffset ?? (fallbackOffset + mapped.length);
  const lim = pagRaw?.limit ?? pagRaw?.Limit ?? fallbackLimit;
  return {
    message: raw?.message ?? raw?.Message ?? 'Success',
    code: raw?.code ?? raw?.Code ?? '200',
    data: mapped,
    pagination: { limit: lim, offset: fallbackOffset, total, hasMore, nextOffset: nextOff }
  };
}

export const getContactsWithFilters = async (
  sectorId: number,
  includeArchived: boolean = false,
  onlyArchived: boolean = false,
  limit: number = 50,
  offset: number = 0,
  view: ContactsListView = 'chat',
  search?: string,
  /** Filtro por canal da conversa (E-mail em Conversas): 'whatsapp' | 'email'; vazio/'all' = todos. */
  channel?: string
): Promise<ApiResponse<Contact>> => {
  try {
    const token = SessionService.getToken();

    if (!token) {
      throw new Error('Authentication token not found');
    }

    const trimmed =
      search != null && String(search).trim() !== ''
        ? String(search).trim().slice(0, 80)
        : undefined;

    // Use chat-optimized POST list endpoint (supports archive filters, search, limit/offset, view natively)
    // Backend: POST /Contact/list and /Contact/contacts/list return exactly { message, code, data, pagination }
    const listPayload = {
      sectorId,
      includeArchived,
      onlyArchived,
      limit,
      offset,
      view,
      search: trimmed,
      channel: channel && channel !== 'all' ? channel : undefined,
    };
    const listCandidates = [
      `${API_URL}/Contact/list`,
      `${API_URL}/Contact/contacts/list`,
      `${API_URL.replace(/\/api$/i, '/api/v1')}/Contact/list`,
      `${API_URL.replace(/\/api$/i, '/api/v1')}/Contact/contacts/list`,
    ];

    const listHeaders = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };

    let responseData: any = null;
    let lastListError: unknown = null;
    for (const u of listCandidates) {
      try {
        responseData = await postJsonWithRetry<any>(u, listPayload, listHeaders);
        break;
      } catch (error) {
        lastListError = error;
      }
    }

    let axiosRes = responseData != null ? { data: responseData } : null;
    if (!axiosRes && lastListError) {
      throw lastListError;
    }

    if (!axiosRes) {
      // last resort: paged GET (no archive server filter)
      const pageNumber = Math.max(1, Math.floor(offset / limit) + 1);
      const pageSize = limit;
      let pagedUrl = `${API_URL}/Contact/paged?sectorId=${sectorId}&pageNumber=${pageNumber}&pageSize=${pageSize}`;
      if (trimmed) pagedUrl += `&search=${encodeURIComponent(trimmed)}`;
      axiosRes = await axios.get<any>(pagedUrl, {
        headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` }
      });
    }

    let apiResp = toApiResponseFromAny(axiosRes.data, limit, offset);

    // Client-side archive filtering as safety net (backend list already applies them)
    if (includeArchived === false || onlyArchived === true) {
      const filtered = apiResp.data.filter((c: Contact) => {
        const arch = !!c.archived;
        if (onlyArchived) return arch;
        if (!includeArchived) return !arch;
        return true;
      });
      const totalFiltered = filtered.length < limit ? (offset + filtered.length) : (apiResp.pagination?.total || 0);
      apiResp = {
        ...apiResp,
        data: filtered,
        pagination: apiResp.pagination ? {
          ...apiResp.pagination,
          total: totalFiltered,
          hasMore: filtered.length === limit
        } : undefined
      };
    }

    return apiResp;
  } catch (error) {
    console.error('Erro ao buscar contatos com filtros (POST list), tentando fallback GetBySector:', error);
    // Fallback to GetBySector (returns all for sector) + client filter + slice
    try {
      const fallback = await getContacts(sectorId);
      let rows: Contact[] = Array.isArray(fallback?.data) ? fallback.data : [];
      rows = rows.filter((c: Contact) => {
        const arch = !!c.archived;
        if (onlyArchived) return arch;
        if (!includeArchived) return !arch;
        return true;
      });
      if (search) {
        const s = String(search).toLowerCase();
        rows = rows.filter(c => (c.name || '').toLowerCase().includes(s) || (c.number || '').includes(s));
      }
      const chunk = rows.slice(offset, offset + limit);
      const hasMore = (offset + limit) < rows.length;
      return {
        message: 'Success',
        code: '200',
        data: chunk,
        pagination: { limit, offset, total: rows.length, hasMore, nextOffset: offset + chunk.length }
      };
    } catch (fbErr) {
      console.error('Fallback também falhou:', fbErr);
      throw error;
    }
  }
};

export interface SectorCounts {
  total: number;
  unread: number;
  archived: number;
  favorites: number;
  groups: number;
}

export const getContactCounts = async (sectorId: number): Promise<SectorCounts> => {
  try {
    const token = SessionService.getToken();
    if (!token) throw new Error('Authentication token not found');

    // Backend supports /Contact/counts/{sectorId} and /Contact/contacts/counts/{sectorId} (and v1 variants)
    // Response shape: { data: { total, unread, archived, favorites, groups } } or wrapped success
    const candidates = [
      `${API_URL}/Contact/contacts/counts/${sectorId}`,
      `${API_URL}/Contact/counts/${sectorId}`,
      `${API_URL.replace(/\/api$/i, '/api/v1')}/Contact/contacts/counts/${sectorId}`,
      `${API_URL.replace(/\/api$/i, '/api/v1')}/Contact/counts/${sectorId}`,
      `${API_URL}/v1/Contact/contacts/counts/${sectorId}`,
    ];
    for (const url of candidates) {
      try {
        const response = await axios.get<any>(url, {
          headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` }
        });
        const body = response.data || {};
        // {data: counts} or {Data: counts} or direct or {message,code,data: counts}
        let counts = body?.data ?? body?.Data ?? body;
        if (counts && (counts.message || counts.code) && counts.data) {
          counts = counts.data;
        }
        if (counts && typeof counts === 'object' && ('total' in counts || 'Total' in counts)) {
          return {
            total: Number(counts.total ?? counts.Total ?? 0),
            unread: Number(counts.unread ?? counts.Unread ?? 0),
            archived: Number(counts.archived ?? counts.Archived ?? 0),
            favorites: Number(counts.favorites ?? counts.Favorites ?? 0),
            groups: Number(counts.groups ?? counts.Groups ?? 0),
          } as SectorCounts;
        }
      } catch {
        // try next candidate
      }
    }
    return { total: 0, unread: 0, archived: 0, favorites: 0, groups: 0 };
  } catch (error) {
    console.error('Erro ao buscar contagens:', error);
    return { total: 0, unread: 0, archived: 0, favorites: 0, groups: 0 };
  }
};

export interface PollNotification {
  id: number;
  sectorId: number;
  contactId: number;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
}

export const pollNotifications = async (sectorId: number): Promise<{ notifications: PollNotification[]; count: number }> => {
  try {
    const token = SessionService.getToken();
    if (!token) throw new Error('Authentication token not found');

    // Backend poll endpoints (Go recovered + C#): /Notification/poll/{sectorId}, /Notification/notifications/poll/{sectorId}
    // (also /Notification/poll/{sectorId} direct). Support v1 variants.
    // Shapes: {data:{notifications, count}}, {message,code,data:{notifications,count}}, array, or {notifications,count}
    const candidates = [
      `${API_URL}/Notification/notifications/poll/${sectorId}`,
      `${API_URL}/Notification/poll/${sectorId}`,
      `${API_URL.replace(/\/api$/i, '/api/v1')}/Notification/notifications/poll/${sectorId}`,
      `${API_URL.replace(/\/api$/i, '/api/v1')}/Notification/poll/${sectorId}`,
      `${API_URL}/Notification/sector/${sectorId}`,
    ];
    for (const url of candidates) {
      try {
        const response = await axios.get<any>(url, {
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        const body = response.data || {};
        // extract payload that may contain notifications array + count
        let payload: any = body?.data ?? body?.Data ?? body;
        if (payload && (payload.message || payload.code) && payload.data) {
          payload = payload.data;
        }
        let notifsRaw: any[] = [];
        let cnt = 0;
        if (Array.isArray(payload)) {
          notifsRaw = payload;
        } else if (payload && typeof payload === 'object') {
          notifsRaw = Array.isArray(payload.notifications) ? payload.notifications :
                      (Array.isArray(payload.Notifications) ? payload.Notifications : []);
          const rawCount = payload.count ?? payload.Count ?? payload.total ?? payload.Total;
          cnt = typeof rawCount === 'number' ? rawCount : notifsRaw.length;
        }
        const mapped: PollNotification[] = notifsRaw.map((n: any) => ({
          id: Number(n.id ?? n.Id ?? 0),
          sectorId: Number(n.sectorId ?? n.SectorId ?? sectorId),
          contactId: Number(n.contactId ?? n.ContactId ?? 0),
          type: String(n.type ?? n.Type ?? ''),
          title: String(n.title ?? n.Title ?? ''),
          message: String(n.message ?? n.Message ?? ''),
          isRead: Boolean(n.isRead ?? n.IsRead ?? false),
          createdAt: String(n.createdAt ?? n.CreatedAt ?? new Date().toISOString()),
        }));
        return { notifications: mapped, count: cnt || mapped.length };
      } catch {
        // try next poll endpoint
      }
    }
    return { notifications: [], count: 0 };
  } catch (error) {
    console.error('Erro ao buscar notificações:', error);
    return { notifications: [], count: 0 };
  }
};

export const markNotificationRead = async (notificationId: number): Promise<void> => {
  try {
    const token = SessionService.getToken();
    if (!token) return;
    await axios.put(
      `${API_URL}/Notification/${notificationId}/read`,
      {},
      { headers: { 'Authorization': `Bearer ${token}` } }
    );
  } catch (error) {
    console.error('Error marking notification as read:', error);
  }
};

export const markAllNotificationsRead = async (sectorId: number): Promise<void> => {
  try {
    const token = SessionService.getToken();
    if (!token) return;
    // C# controller has no read-all; call per or no-op
    await axios.put(
      `${API_URL}/Notification/read-all/${sectorId}`,
      {},
      { headers: { 'Authorization': `Bearer ${token}` } }
    ).catch(() => {/* optional endpoint */});
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
  }
};

export const deleteNotificationsByContact = async (contactId: number): Promise<void> => {
  try {
    const token = SessionService.getToken();
    if (!token) return;
    await axios.delete(
      `${API_URL}/Notification/contact/${contactId}`,
      { headers: { 'Authorization': `Bearer ${token}` } }
    ).catch(() => {/* optional */});
  } catch (error) {
    console.error('Error deleting notifications for contact:', error);
  }
};

export const importContacts = async (sectorId: number, contacts: { name: string; number: string; sectorId: number }[]): Promise<any> => {
  try {
    const token = SessionService.getToken();
    if (!token) throw new Error('Authentication token not found');

    const response = await axios.post(
      `${API_URL}/Contact/import`,
      {
        sectorId,
        contacts
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Erro ao importar contatos:', error);
    throw error;
  }
};

/** Minimal message shape returned after parsing backend wrappers. */
export interface ContactMessage {
  id: number;
  content: string | null;
  mediaType: string;
  mediaUrl: string | null;
  fileName: string | null;
  mimeType: string | null;
  sectorId: number;
  contactID: number;
  sentAt: string;
  isSent: boolean;
  isRead: boolean;
  senderName?: string;
  repliedToId?: any;
  repliedContent?: string;
  repliedSender?: string;
}

export interface GetContactMessagesResult {
  messages: ContactMessage[];
  total: number;
  hasMore: boolean;
  pagination?: {
    limit: number;
    offset: number;
    total: number;
    hasMore: boolean;
    nextOffset: number;
  };
  contact?: any;
}

export { getMessagesByContactIdWithPagination as getMessages } from './MessageService';
